import { useModal } from '@ebay/nice-modal-react'
import { styled } from '@linaria/react'
import { Link } from '@tanstack/router'
import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import bronze from '../../assets/vip/bronze.svg'
import gold from '../../assets/vip/gold.svg'
import platinum from '../../assets/vip/platinum.svg'
import silver from '../../assets/vip/silver.svg'
import { MAX_BET } from '../../constants/vip'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { VipLevelObj } from '../../types/vip'
import { api } from '../../utils/api-client'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import RedeemModal from '../profile/redeem-modal'

const ProgressBar = styled.div`
  border: 1px solid #525d76;
  padding: 4px;
  border-radius: 10px;
  width: 100%;
  margin: 0 12px;
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #fbb03b;
  }
  .bar-container {
    height: 6px;
    flex: 1;
    position: relative;
  }
  .bar {
    position: absolute;
    top: 0;
    left: -3px;
    width: 0%;
    height: 6px;
    background-color: #fbb03b;
    border-radius: 2px;
  }
`

const FirstRow = styled.div`
  width: 100%;
  padding: 0 30px 7px 0;
`

const VipIcon = styled.img`
  width: 40px;
  max-height: 40px;
`

const Member = styled.p`
  font-family: 'Syncopate-Bold';
  text-transform: uppercase;
  color: var(--txt);
  font-size: 14px;
`

const VipAccess = styled.span`
  color: #e9bf2e;
  font-family: 'Syncopate-Bold';
  text-transform: uppercase;
  font-size: 14px;
  white-space: nowrap;
  border-bottom: 1px solid #e9bf2e;
  margin-right: 5px;
`

const Percentage = styled.p`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  font-family: 'Avenir-Black';
  color: var(--txt);
  font-size: 11px;
  text-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 1;
`

export const PointsBadgeRedeem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100px;
  position: relative;
  .label {
    font-size: 10px;
    color: var(--txt);
  }
  .value {
    font-size: 10px;
    color: #fbb03b;
    margin-bottom: 3px;
  }
  .redeem {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 8px;
    padding: 4px 8px;
    background-color: #df0033;
    color: white;
    border-radius: 2px;
    transition: all 0.2s ease-in-out;
    opacity: 1;
    cursor: pointer;
    font-family: 'Syncopate-Bold';
    text-transform: uppercase;
  }
  @media (min-width: 1200px) {
    line-height: 14px;
    width: 120px;
    .label,
    .value {
      font-size: 12px;
    }
    .label {
      margin-bottom: 3px;
    }
    .value {
      margin-bottom: 5px;
    }
    .redeem {
      font-size: 10px;
      padding: 6px 10px;
    }
  }
`

export const PointsBadge = styled.div`
  background-color: var(--tertiary);
  border-radius: 5px;
  text-align: center;
  width: 100px;
  padding: 6px 0 5px;
  line-height: 12px;
  .label {
    font-size: 10px;
    color: var(--txt);
    margin-bottom: 2px;
  }
  .value {
    font-size: 10px;
    color: #fbb03b;
  }
  @media (min-width: 1200px) {
    line-height: 14px;
    width: 120px;
    .label,
    .value {
      font-size: 12px;
    }
  }
`

const VipLevel = () => {
  const username = useBoundStore(state => state.user.username)
  const { t } = useTranslation(['personalData', 'referral', 'vip'])
  const [vipLevel, setVipLevel] = useState<VipLevelObj>({
    viplevel: '0',
    accountnum: 0,
  })
  const redeemPoints = useBoundStore(state => state.settings.redeem_points)
  const redeemModal = useModal(RedeemModal)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const fetchVipLevel = async () => {
      try {
        const response = await api.post<ApiResponse<VipLevelObj>>(
          '/ajax_data.php',
          {
            type: 'get_vip_level',
          },
          { signal }
        )
        const { status, info } = response.data
        if (status === 1) {
          setVipLevel(info)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchVipLevel()

    return () => {
      controller.abort()
    }
  }, [username])

  const getCurrentVip = useCallback(() => {
    const { viplevel: level } = vipLevel
    if (level === '0')
      return {
        src: bronze,
        label: t('bronze', { ns: 'vip' }),
      }
    if (level === '1')
      return {
        src: silver,
        label: t('silver', { ns: 'vip' }),
      }
    if (level === '2')
      return {
        src: gold,
        label: t('gold', { ns: 'vip' }),
      }
    if (level === '3')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    return {
      src: bronze,
      label: t('bronze', { ns: 'vip' }),
    }
  }, [vipLevel])

  const getNextVip = useCallback(() => {
    const { viplevel: level } = vipLevel
    if (level === '0')
      return {
        src: silver,
        label: t('silver', { ns: 'vip' }),
      }
    if (level === '1')
      return {
        src: gold,
        label: t('gold', { ns: 'vip' }),
      }
    if (level === '2')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    if (level === '3')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    return {
      src: bronze,
      label: t('bronze', { ns: 'vip' }),
    }
  }, [vipLevel])

  const getProgress = useCallback(() => {
    const { viplevel: level, accountnum } = vipLevel
    if (level === '3') return 100
    const nextVipLevel = (
      parseInt(vipLevel.viplevel) + 1
    ).toString() as VipLevelObj['viplevel']

    return (accountnum / MAX_BET[nextVipLevel]) * 100
  }, [vipLevel])

  const progress = getProgress()
  const currentVip = getCurrentVip()
  const nextVip = getNextVip()

  return (
    <div className="v-flx-ctr">
      <div
        className="flx-btw-ctr"
        style={{ width: '100%', marginBottom: '15px', paddingRight: '30px' }}>
        <Member>
          {currentVip.label.charAt(0) + currentVip.label.slice(1).toLowerCase()}
        </Member>
        {redeemPoints.value ? (
          <PointsBadgeRedeem>
            <p className="label">{t('total-points', { ns: 'personalData' })}</p>
            <p className="value">{numberWithCommas(+vipLevel.accountnum)}</p>
            <button className="redeem" onClick={() => redeemModal.show()}>
              {t('redeem', { ns: 'referral' })}
            </button>
          </PointsBadgeRedeem>
        ) : (
          <PointsBadge>
            <p className="label">{t('total-points', { ns: 'personalData' })}</p>
            <p className="value">{numberWithCommas(+vipLevel.accountnum)}</p>
          </PointsBadge>
        )}
        <div className="flx-ctr">
          <Link to="/vip" search={{ tab: 'privileges' }}>
            <VipAccess>{t('vip-access', { ns: 'personalData' })}</VipAccess>
          </Link>
          <Icon
            id="chevron-right"
            width="8"
            height="16"
            fill="#E9BF2E"
            style={{ marginTop: '1px', display: 'block' }}
          />
        </div>
      </div>
      <FirstRow className="flx-ctr">
        <VipIcon src={currentVip.src} />
        <ProgressBar className="flx-ctr">
          <div className="dot" />
          <div className="bar-container">
            <Percentage>{progress.toFixed(2)}%</Percentage>
            <div
              className="bar"
              style={{ width: `calc(${progress}% + 3px)` }}
            />
          </div>
          <div className="dot" />
        </ProgressBar>
        <VipIcon src={nextVip.src} />
      </FirstRow>
    </div>
  )
}

export default VipLevel
